import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailCashShiftCloses,
  type CashShiftClose,
  type FiscalZStatus,
} from '@/api/retailCashShiftClose';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const PERMISSION = 'retail.shift.close';

const Z_STATUS_LABEL: Record<string, string> = {
  not_fiscalized: '— Z-звіт не сформ.',
  pending: '⏳ В очікуванні',
  fiscalized: '✓ Z-звіт',
  failed: '⚠ Помилка',
  demo: 'D Демо',
};

export default function CashShiftClosesPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [zFilter, setZFilter] = useState<string>('');
  useEffect(() => {
    const first = orgList.data?.items[0];
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-cash-shift-close', orgId, zFilter],
    queryFn: () =>
      retailCashShiftCloses.list({
        organization_id: orgId || undefined,
        fiscal_z_status: zFilter ? (zFilter as FiscalZStatus) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<CashShiftClose>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата/час закриття',
        width: 180,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'pos_workstation_id',
        header: 'POS',
        width: 150,
        sortValue: (r) => r.pos_workstation_id ?? '',
        cell: (r) => r.pos_workstation_id,
      },
      {
        key: 'total_sales_count',
        header: 'К-сть чеків',
        width: 130,
        align: 'right',
        sortValue: (r) => r.total_sales_count,
        cell: (r) => r.total_sales_count,
      },
      {
        key: 'total_sales_amount',
        header: 'Сума продажів',
        width: 160,
        align: 'right',
        sortValue: (r) => Number(r.total_sales_amount),
        cell: (r) =>
          r.total_sales_amount
            ? Number(r.total_sales_amount).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'cash_difference',
        header: 'Розбіжність',
        width: 140,
        align: 'right',
        sortValue: (r) => (r.cash_difference !== null && r.cash_difference !== undefined ? Number(r.cash_difference) : 0),
        cell: (r) =>
          r.cash_difference !== null && r.cash_difference !== undefined
            ? Number(r.cash_difference).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '—',
      },
      {
        key: 'fiscal_z_status',
        header: 'Z-звіт ПРРО',
        width: 170,
        sortValue: (r) => r.fiscal_z_status ?? '',
        cell: (r) => Z_STATUS_LABEL[r.fiscal_z_status] ?? r.fiscal_z_status,
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав закривати зміну (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Закриття зміни (Z-звіт)
          <HintBadge
            title="Що це?"
            text={
              'Документ закриття касирської зміни — підбиття підсумків + ПРРО Z-звіт.\n\n' +
              'Що відбувається при закритті:\n' +
              '• Backend агрегує усі чеки і повернення цієї зміни (SUM кешу/карток/бонусів).\n' +
              '• Касир вводить ФАКТИЧНУ готівку (скільки реально у касі).\n' +
              '• Система обчислює розбіжність: фактично - очікувано.\n\n' +
              'Сценарії розбіжностей:\n' +
              '• Нестача (мінус) → касир обирає: «Списати» (Дт 947) або «Стягнути з касира» (Дт 375).\n' +
              '• Лишок (плюс) → автоматична проводка Дт 301 — Кт 719 (інші доходи).\n' +
              '• Iнкасація — частина готівки передається у банк / інкасатору.\n\n' +
              'Окремо: «Фіскалізувати Z-звіт» → ПРРО формує підсумковий звіт за день у ДПС.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.code} — {o.name}
            </option>
          ))}
        </select>
        <select
          value={zFilter}
          onChange={(e) => setZFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси Z-звіту</option>
          <option value="not_fiscalized">Не сформовано</option>
          <option value="pending">В очікуванні</option>
          <option value="fiscalized">Сформовано</option>
          <option value="failed">Помилка</option>
          <option value="demo">Демо</option>
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/retail/cash-shift-close/new')}
        >
          + Закриття зміни
        </button>
      </div>
      <DataTable<CashShiftClose>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/retail/cash-shift-close/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
